import api from './api.services'
import { ActivityEntry } from './api.types'
import { offlineActivityQueue, QueuedActivity } from './OfflineActivityQueue'
import { networkMonitor, NetworkStatus } from './NetworkMonitor'

// Upload result types
export interface UploadResult {
  success: boolean
  activityId?: string
  error?: string
  wasQueued?: boolean
}

// Upload statistics
export interface UploadStats {
  queuedCount: number
  isOnline: boolean
  isProcessing: boolean
  lastProcessedAt?: number
}

// Event types for upload status
export type UploadEventType = 'upload_success' | 'upload_failed' | 'queue_added' | 'queue_processed' | 'network_changed'

export interface UploadEvent {
  type: UploadEventType
  data?: any
  timestamp: number
}

type UploadEventListener = (event: UploadEvent) => void

/**
 * Service for uploading activities with offline support and retry logic
 * Combines network monitoring, queueing, and automatic retry functionality
 */
export class ActivityUploadService {
  private listeners: Set<UploadEventListener> = new Set()
  private isProcessing = false
  private lastProcessedAt?: number
  private networkRemoveListener?: () => void

  /**
   * Initialize the upload service
   * Sets up network monitoring and starts background processing
   */
  async initialize(): Promise<void> {
    console.log('🚀 ActivityUploadService: Initializing...')

    // Initialize network monitor
    await networkMonitor.initialize()

    // Set up network change listener
    this.networkRemoveListener = networkMonitor.addListener(this.handleNetworkChange.bind(this))

    // Start background processing for queued items
    this.startBackgroundProcessing()

    console.log('✅ ActivityUploadService: Initialized')
  }

  /**
   * Upload an activity (with offline support)
   * @param activity - The activity data to upload
   * @param routeData - Optional GPS route data
   * @returns Upload result
   */
  async uploadActivity(activity: ActivityEntry, routeData?: any[]): Promise<UploadResult> {
    console.log('📤 ActivityUploadService: Uploading activity', {
      activityType: activity.activityType,
      duration: activity.duration,
    })

    // Try to upload immediately - let the network error tell us if we're offline
    try {
      const { activityId } = await this.performUpload(activity, routeData)

      console.log('✅ ActivityUploadService: Upload successful')
      return {
        success: true,
        activityId,
      }
    } catch (error) {
      // Parse error to understand what went wrong
      const { errorMessage, errorDetails } = this.parseUploadError(error)

      console.log('❌ ActivityUploadService: Upload failed, adding to queue', {
        error: errorMessage,
        activity: { type: activity.activityType, duration: activity.duration },
      })

      // Add to offline queue for retry
      const queueId = await offlineActivityQueue.addToQueue(activity, routeData)

      this.emitEvent({
        type: 'upload_failed',
        data: { activity, error: errorMessage, queueId },
        timestamp: Date.now(),
      })

      return { success: false, wasQueued: true, error: errorMessage }
    }
  }

  /**
   * Manually process the offline queue
   * Useful for forcing a retry when back online
   */
  async processQueue(): Promise<void> {
    if (this.isProcessing) {
      console.log('⏳ ActivityUploadService: Already processing queue')
      return
    }

    // Refresh network status before processing queue
    const networkStatus = await networkMonitor.refresh()
    if (!networkStatus.connected) {
      console.log("📴 ActivityUploadService: Can't process queue - offline")
      return
    }

    this.isProcessing = true
    console.log('🔄 ActivityUploadService: Processing offline queue...')

    try {
      const readyItems = offlineActivityQueue.getReadyForRetry()
      console.log(`📦 ActivityUploadService: Found ${readyItems.length} items ready for retry`)

      for (const item of readyItems) {
        if (!networkMonitor.isOnline()) {
          console.log('📴 ActivityUploadService: Lost connection during processing')
          break
        }

        await this.processQueueItem(item)

        // Small delay between uploads to avoid overwhelming the server
        await this.sleep(100)
      }

      this.lastProcessedAt = Date.now()

      this.emitEvent({
        type: 'queue_processed',
        data: { processedCount: readyItems.length },
        timestamp: Date.now(),
      })
    } catch (error) {
      console.error('❌ ActivityUploadService: Error processing queue', error)
    } finally {
      this.isProcessing = false
    }
  }

  /**
   * Get current upload statistics
   * @returns Current stats
   */
  getStats(): UploadStats {
    return {
      queuedCount: offlineActivityQueue.getQueueCount(),
      isOnline: networkMonitor.isOnline(),
      isProcessing: this.isProcessing,
      lastProcessedAt: this.lastProcessedAt,
    }
  }

  /**
   * Add event listener for upload events
   * @param listener - Function to call on events
   * @returns Function to remove the listener
   */
  addListener(listener: UploadEventListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * Clear all queued activities
   */
  async clearQueue(): Promise<void> {
    await offlineActivityQueue.clearQueue()
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    if (this.networkRemoveListener) {
      this.networkRemoveListener()
    }
    this.listeners.clear()
  }

  /**
   * Helper method to parse and format upload errors consistently
   * @param error - The error object to parse
   * @param context - Additional context for error message formatting
   * @returns Formatted error message and structured error details
   */
  private parseUploadError(
    error: any,
    context: {
      isRetry?: boolean
      defaultMessage?: string
    } = {},
  ): { errorMessage: string; errorDetails: any } {
    const { isRetry = false, defaultMessage = 'Upload failed' } = context
    let errorMessage = defaultMessage
    const errorDetails: any = {}

    // Extract basic error info if it's an Error instance
    if (error instanceof Error) {
      errorMessage = error.message
      errorDetails.name = error.name
      errorDetails.stack = error.stack?.split('\n').slice(0, 3).join('\n') // First 3 lines of stack
    }

    // Check for specific axios/network errors
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as any
      errorDetails.isAxiosError = true
      errorDetails.status = axiosError.response?.status
      errorDetails.statusText = axiosError.response?.statusText
      errorDetails.responseData = axiosError.response?.data
      const prefix = isRetry ? 'Retry HTTP' : 'HTTP'
      errorMessage = `${prefix} ${axiosError.response?.status}: ${axiosError.response?.statusText || 'Unknown error'}`
    } else if (error && typeof error === 'object' && 'request' in error) {
      errorDetails.isNetworkError = true
      const prefix = isRetry ? 'Retry network request failed' : 'Network request failed'
      errorMessage = `${prefix} - no response received`
    } else if (error && typeof error === 'object' && 'code' in error) {
      errorDetails.errorCode = (error as any).code
      const prefix = isRetry ? 'Retry ' : ''
      if ((error as any).code === 'ECONNABORTED') {
        errorMessage = `${prefix}request timeout`
      } else if ((error as any).code === 'ENOTFOUND') {
        errorMessage = `${prefix}DNS lookup failed`
      }
    }

    return { errorMessage, errorDetails }
  }

  /**
   * Helper method to perform the actual upload
   * @param activity - The activity to upload
   * @param routeData - Optional GPS route data
   * @param isRetry - Whether this is a retry attempt
   * @param queueId - Queue ID if this is a retry
   * @returns The upload response and generated activity ID
   */
  private async performUpload(
    activity: ActivityEntry,
    routeData?: any[],
    isRetry: boolean = false,
    queueId?: string,
  ): Promise<{ response: any; activityId: string }> {
    const logPrefix = isRetry ? '🔄' : '🚀'
    const uploadType = isRetry ? 'retry' : 'upload'

    console.log(`${logPrefix} ActivityUploadService: Attempting ${uploadType}...`)

    // Log GPS route data if available
    if (routeData && routeData.length > 0) {
      console.log(`🗺️ ActivityUploadService: Uploading activity with ${routeData.length} GPS route points`)
      console.log('🗺️ Route data sample (first 3 points):', routeData.slice(0, 3))
    } else {
      console.log('📍 ActivityUploadService: No GPS route data to upload')
    }

    // Create upload request with route data if available
    const uploadRequest: any = { activity }
    if (routeData && routeData.length > 0) {
      uploadRequest.route = routeData
      console.log('📤 ActivityUploadService: Including route data in upload request')
    } else {
      console.log('📤 ActivityUploadService: Upload request contains only activity data (no route)')
    }

    const response = await api.activity.uploadActivity(uploadRequest)

    console.log(
      `✅ ActivityUploadService: ${uploadType} successful` +
        (response.status ? ` (HTTP ${response.status})` : ' (no status code)'),
      response.data,
    )

    // Try to get activityId from response data if available, otherwise generate one
    const activityId = response.data?.activityId || `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    // Emit success event
    this.emitEvent({
      type: 'upload_success',
      data: {
        activity,
        activityId,
        ...(isRetry && { wasRetry: true }),
        ...(queueId && { queueId }),
      },
      timestamp: Date.now(),
    })

    return { response, activityId }
  }

  /**
   * Process a single queue item
   */
  private async processQueueItem(item: QueuedActivity): Promise<void> {
    try {
      console.log('🔄 ActivityUploadService: Retrying upload', {
        id: item.id,
        retryCount: item.retryCount,
        activityType: item.activity.activityType,
      })

      const { activityId } = await this.performUpload(item.activity, item.routeData, true, item.id)

      console.log('✅ ActivityUploadService: Retry successful', {
        id: item.id,
      })

      // Remove from queue
      await offlineActivityQueue.removeFromQueue(item.id)
    } catch (error) {
      // Parse error using helper method
      const { errorMessage, errorDetails } = this.parseUploadError(error, {
        isRetry: true,
        defaultMessage: 'Retry failed',
      })

      console.error('❌ ActivityUploadService: Retry failed', {
        id: item.id,
        retryCount: item.retryCount,
        error: errorMessage,
        errorDetails,
      })

      // Mark as failed (will schedule next retry or remove if max retries exceeded)
      await offlineActivityQueue.markAsFailed(item.id, errorMessage)
    }
  }

  /**
   * Handle network status changes
   */
  private handleNetworkChange(status: NetworkStatus): void {
    console.log('🌐 ActivityUploadService: Network status changed', status)

    this.emitEvent({
      type: 'network_changed',
      data: status,
      timestamp: Date.now(),
    })

    // If we're back online, process the queue
    if (status.connected && !this.isProcessing) {
      // Small delay to let network stabilize
      setTimeout(() => {
        this.processQueue()
      }, 1000)
    }
  }

  /**
   * Start background processing for the queue
   */
  private startBackgroundProcessing(): void {
    // Process queue every 30 seconds if online
    setInterval(() => {
      if (networkMonitor.isOnline() && !this.isProcessing) {
        const readyCount = offlineActivityQueue.getReadyForRetry().length
        if (readyCount > 0) {
          console.log(`⏰ ActivityUploadService: Background processing ${readyCount} ready items`)
          this.processQueue()
        }
      }
    }, 30000)
  }

  /**
   * Emit an event to all listeners
   */
  private emitEvent(event: UploadEvent): void {
    this.listeners.forEach((listener) => {
      try {
        listener(event)
      } catch (error) {
        console.error('❌ ActivityUploadService: Error in event listener', error)
      }
    })
  }

  /**
   * Sleep utility function
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}

// Export singleton instance
export const activityUploadService = new ActivityUploadService()
